import { useEffect } from 'react';

import {
  DIABETES_INCLUSION_LEGACY_SCREENER_KEYS,
  DYNAMIC_LEGACY_SCREENER_KEYS,
} from '@/modules/api';
import { useRulesEngine } from '@/modules/rulesEngine';
import { LoadingScreen } from '@/modules/ui';

import { LegacyAssignedScreenerFlow } from './LegacyAssignedScreenerFlow';

/**
 * The test-arm exclusion once `diabetes_inclusion_v1` has completed. Hoisted to
 * module scope, NOT built inline in the component: `useAssignedScreener` requires
 * `excludeKeys` to be a stable reference because it is an effect dependency, and
 * that effect opens with `setIsLoading(true)` and re-issues both the fetch and the
 * `createProgramUnitInstance` POST. A fresh array per render would therefore
 * unmount the survey mid-screener (discarding entered answers) and re-run a write
 * on any incidental re-render of the provider above — e.g. PostHog re-emitting
 * `onFeatureFlags`.
 */
const EXCLUDE_KEYS_WITH_DIABETES_INCLUSION = [
  ...DYNAMIC_LEGACY_SCREENER_KEYS,
  ...DIABETES_INCLUSION_LEGACY_SCREENER_KEYS,
] as const;

/**
 * The onboarding legacy assigned-screeners step (`/step/assigned-screeners`) —
 * the backend-assigned screeners flow, served in BOTH experiment arms:
 *   - control arm → serves every backend-assigned screener (no exclusion). The
 *     resolver walks control here via the linear survey order (health-conditions
 *     → health-goals → assigned-screeners).
 *   - test arm → runs AFTER the dynamic-assessment pass (`/step/dynamic-assessment`)
 *     as the safety net for any screener dynamic assessment doesn't support yet,
 *     excluding the migrated keys so they aren't re-asked. Today every assigned
 *     screener is migrated, so this typically fetches an empty list and advances.
 *
 * The `onb_use_dynamic_assessment` flag is read only to pick `excludeKeys` (the
 * dynamic pass itself lives on `DynamicAssessmentScreen` now). Until the flag
 * resolves, hold a spinner so the flow mounts with the correct exclusion (its
 * data hook fires on mount). An `'error'` read fails open to control (no
 * exclusion), as does a latched `dynamicAssessmentErrored`: if the dynamic pass
 * failed, its screeners were never served, so this legacy pass must serve them
 * all rather than exclude the "migrated" keys and drop them entirely.
 *
 * The `DIABETES_INCLUSION_LEGACY_SCREENER_KEYS` keys need one more condition
 * than the rest: `diabetes_inclusion_v1` has no terminal effect completing
 * those legacy ProgramUnitInstances, so excluding them just because we're in
 * the test arm would silently drop the questions entirely for a member whose
 * `diabetes_inclusion_v1` pass was abandoned or never published — the same
 * failure mode `dynamicCompletedFlowKeys` already guards against for the
 * standalone pre-diabetes / phq-gad screens. Only exclude them once that flow
 * key is actually in `dynamicCompletedFlowKeys`.
 *
 * Known gap: that gate does not survive a reload. `dynamicCompletedFlowKeys` is
 * in-memory state, so a member who finishes the dynamic pass and then refreshes
 * (or is relaunched on native) comes back with an empty list and is asked the
 * three diabetes screeners again. The other five keys have a backstop — their
 * legacy ProgramUnitInstances were completed server-side, so `_maybe_assign_*`
 * drops them regardless — but nothing completes these three, which is the whole
 * premise here. It fails in the safe direction (asked twice rather than never),
 * so this is a narrowing of the double-ask, not a complete fix.
 */
export function AssignedScreenersScreen() {
  const {
    dynamicAssessmentEnabled,
    ensureDynamicAssessment,
    dynamicAssessmentErrored,
    dynamicCompletedFlowKeys,
  } = useRulesEngine();

  // Deduped; the driver usually read the flag already to pick the survey start.
  useEffect(() => {
    ensureDynamicAssessment();
  }, [ensureDynamicAssessment]);

  // Hold the spinner until the flag resolves so the flow mounts once with the
  // right `excludeKeys` (control → none; test → the migrated keys). An `'error'`
  // read counts as resolved and fails open to control.
  const resolved =
    dynamicAssessmentEnabled.status === 'ready' || dynamicAssessmentEnabled.status === 'error';
  if (!resolved) {
    return <LoadingScreen />;
  }

  const isTestArm =
    dynamicAssessmentEnabled.status === 'ready' && dynamicAssessmentEnabled.value === true;

  // Test arm excludes the migrated keys (already answered + written via the
  // dynamic terminal effects); control serves every screener. If the dynamic
  // pass errored, exclude nothing — those screeners weren't served, so a
  // stale/back-nav landing here must still ask them (mirrors the pre-split
  // `dynamicErrored ? undefined : keys` contract).
  // Selects between three stable references rather than composing a new array —
  // see EXCLUDE_KEYS_WITH_DIABETES_INCLUSION for why identity matters here. The
  // diabetes keys are gated on completion, not just arm membership (doc comment
  // above).
  const excludeKeys =
    !isTestArm || dynamicAssessmentErrored
      ? undefined
      : dynamicCompletedFlowKeys.includes('diabetes_inclusion_v1')
        ? EXCLUDE_KEYS_WITH_DIABETES_INCLUSION
        : DYNAMIC_LEGACY_SCREENER_KEYS;

  return <LegacyAssignedScreenerFlow excludeKeys={excludeKeys} />;
}
